// Reads a deployment's data_upload_status: one verdict, one route.
import { useCallback, useEffect, useRef, useState } from 'react';
import clientService from 'src/clientService';

const useDeploymentUploadStatus = (
  deploymentId?: string | null,
  active = true,
) => {
  const [complete, setComplete] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const read = useRef(0);

  const refresh = useCallback(() => {
    const seq = ++read.current;
    if (!deploymentId) {
      setComplete(false);
      return Promise.resolve(false);
    }
    return clientService.api
      .getDeploymentDetailsAdminApiV1AdminDeploymentsDeploymentIdGet(deploymentId)
      .then((response) => {
        const raised = response.data?.deployment?.data_upload_status === true;
        if (seq === read.current) setComplete(raised);
        return raised;
      })
      .catch(() => {
        if (seq === read.current) setComplete(false);
        return false;
      });
  }, [deploymentId]);

  useEffect(() => {
    if (!active) {
      read.current += 1;
      setComplete(false);
      setError(null);
      return;
    }
    refresh();
  }, [active, refresh]);

  const markComplete = useCallback(async () => {
    if (!deploymentId) return false;
    setMarking(true);
    setError(null);
    try {
      await clientService.api.updateApiDeploymentApiV1AdminDeploymentsDeploymentIdPut(
        deploymentId,
        { data_upload_status: true },
      );
      if (!(await refresh())) {
        setError(
          'The server still reports this deployment as open, so uploads remain available.',
        );
        return false;
      }
      return true;
    } catch {
      setError('The deployment could not be marked as data-uploaded.');
      return false;
    } finally {
      setMarking(false);
    }
  }, [deploymentId, refresh]);

  return { complete, marking, error, markComplete };
};

export default useDeploymentUploadStatus;
